import {
  Alert,
  Button,
  Group,
  Modal,
  MultiSelect,
  Select,
  Stack,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { IconAlertCircle } from "@tabler/icons-react";
import { useCallback, useMemo, useState } from "react";
import { normalizeDateOnlyQueryRange } from "../../services/dateOnlyQuery";
import type { ShippingFilterOption } from "../../services/workflows.service";
import {
  getLabTodayDateOnly,
  parseDateOnly,
  type DateOnlyString,
} from "../../utils/dateOnly";

export interface ShippedCaseFilterValues {
  caseType: "remote" | "tc_return" | null;
  pathologists: string[];
  sendingPractices: string[];
  dateShippedFrom: DateOnlyString | null;
  dateShippedTo: DateOnlyString | null;
}

interface ShippedCasesFilterModalProps {
  opened: boolean;
  filters: ShippedCaseFilterValues;
  caseTypeOptions: ShippingFilterOption[];
  pathologistOptions: ShippingFilterOption[];
  sendingPracticeOptions: ShippingFilterOption[];
  onApply: (filters: ShippedCaseFilterValues) => void;
  onClose: () => void;
}

export default function ShippedCasesFilterModal({
  opened,
  filters,
  caseTypeOptions,
  pathologistOptions,
  sendingPracticeOptions,
  onApply,
  onClose,
}: ShippedCasesFilterModalProps) {
  const [pendingFilters, setPendingFilters] =
    useState<ShippedCaseFilterValues>(filters);
  const [dateRangeError, setDateRangeError] = useState<string | null>(null);
  const labToday = useMemo(() => getLabTodayDateOnly(), []);

  const handleOpened = useCallback(() => {
    setPendingFilters(filters);
    setDateRangeError(null);
  }, [filters]);

  const handleCaseTypeChange = useCallback((value: string | null) => {
    setPendingFilters((current) => ({
      ...current,
      caseType: value as "remote" | "tc_return" | null,
    }));
  }, []);

  const handlePathologistsChange = useCallback((value: string[]) => {
    setPendingFilters((current) => ({ ...current, pathologists: value }));
  }, []);

  const handleSendingPracticesChange = useCallback((value: string[]) => {
    setPendingFilters((current) => ({
      ...current,
      sendingPractices: value,
    }));
  }, []);

  const handleFromChange = useCallback((value: unknown) => {
    const parsed = value === null ? null : parseDateOnly(value);
    if (value !== null && !parsed) {
      setDateRangeError("Select a valid Date shipped from.");
      return;
    }
    setPendingFilters((current) => ({
      ...current,
      dateShippedFrom: parsed ?? null,
    }));
    setDateRangeError(null);
  }, []);

  const handleToChange = useCallback((value: unknown) => {
    const parsed = value === null ? null : parseDateOnly(value);
    if (value !== null && !parsed) {
      setDateRangeError("Select a valid Date shipped to.");
      return;
    }
    setPendingFilters((current) => ({
      ...current,
      dateShippedTo: parsed ?? null,
    }));
    setDateRangeError(null);
  }, []);

  const handleClearAll = useCallback(() => {
    setPendingFilters({
      caseType: null,
      pathologists: [],
      sendingPractices: [],
      dateShippedFrom: null,
      dateShippedTo: null,
    });
    setDateRangeError(null);
  }, []);

  const handleApply = useCallback(() => {
    try {
      normalizeDateOnlyQueryRange(
        {
          from: pendingFilters.dateShippedFrom ?? undefined,
          to: pendingFilters.dateShippedTo ?? undefined,
        },
        { from: "Date shipped from", to: "Date shipped to" }
      );
      setDateRangeError(null);
      onApply(pendingFilters);
    } catch {
      setDateRangeError(
        "Date shipped from must be before or equal to Date shipped to."
      );
    }
  }, [onApply, pendingFilters]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      onEnterTransitionEnd={handleOpened}
      title="Filter shipped cases"
      size="lg"
      centered
      closeOnClickOutside={false}
    >
      <Stack gap="md">
        {dateRangeError && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Check the shipped date range"
            color="red"
            role="alert"
          >
            {dateRangeError}
          </Alert>
        )}
        <Select
          label="Case Type"
          placeholder="All types"
          data={caseTypeOptions}
          value={pendingFilters.caseType}
          onChange={handleCaseTypeChange}
          clearable
        />
        <MultiSelect
          label="Pathologist"
          placeholder="All pathologists"
          data={pathologistOptions}
          value={pendingFilters.pathologists}
          onChange={handlePathologistsChange}
          searchable
          clearable
        />
        <MultiSelect
          label="Sending Practice"
          placeholder="All practices"
          data={sendingPracticeOptions}
          value={pendingFilters.sendingPractices}
          onChange={handleSendingPracticesChange}
          searchable
          clearable
        />
        <Group grow>
          <DateInput
            label="Date shipped from"
            placeholder="Select date"
            value={pendingFilters.dateShippedFrom}
            onChange={handleFromChange}
            maxDate={labToday}
            clearable
          />
          <DateInput
            label="Date shipped to"
            placeholder="Select date"
            value={pendingFilters.dateShippedTo}
            onChange={handleToChange}
            maxDate={labToday}
            clearable
          />
        </Group>
        <Group justify="space-between" mt="md">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Group gap="sm">
            <Button variant="subtle" onClick={handleClearAll}>
              Clear All
            </Button>
            <Button onClick={handleApply}>Apply Filters</Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
